import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@akuna/ui";
import { SearchField } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { XmlImportItem, XmlImportItemSelection } from "@/hooks/useXmlImport";
import { Item, PaginatedItems, useItems } from "@/hooks/useItems";
import { useProductSubtypes } from "@/hooks/useProductSubtypes";
import { Package, AlertCircle } from "lucide-react";
import { getDefaultStockEntryQuantity } from "@/lib/xml-import/stock-entry";

interface XmlItemSelectionTableProps {
  xmlItems: XmlImportItem[];
  onSelectionChange: (selections: XmlImportItemSelection[]) => void;
  showStock: boolean;
  showFiscal: boolean;
  alreadyImported: boolean;
}

export function XmlItemSelectionTable({
  xmlItems,
  onSelectionChange,
  showStock,
  showFiscal,
  alreadyImported,
}: XmlItemSelectionTableProps) {
  const { t, i18n } = useTranslation("erp");
  const [selections, setSelections] = useState<Map<string, XmlImportItemSelection>>(new Map());
  const [searchTerm, setSearchTerm] = useState("");
  const previousAlreadyImported = useRef(alreadyImported);

  // Get existing items to check for matches
  const { data: existingItemsData } = useItems({ isActive: undefined });
  const existingItems = useMemo<Item[]>(() => {
    if (!existingItemsData) return [];
    if (Array.isArray(existingItemsData)) return existingItemsData;
    return (existingItemsData as PaginatedItems).data ?? [];
  }, [existingItemsData]);
  const { data: productSubtypes = [] } = useProductSubtypes();

  // Initialize selections with default "skip" action
  useEffect(() => {
    const initialSelections = new Map<string, XmlImportItemSelection>();
    xmlItems.forEach((xmlItem) => {
      // Try to find existing item by barcode/EAN or SKU
      const existingItem = existingItems.find(
        (item) =>
          (item.barcode && xmlItem.ean && item.barcode === xmlItem.ean) ||
          (item.sku && xmlItem.codigo && item.sku === xmlItem.codigo)
      );

      initialSelections.set(xmlItem.codigo, {
        xmlItem,
        action: existingItem ? "update" : "create",
        existingItemId: existingItem?.id,
        stockQuantity: getDefaultStockEntryQuantity(xmlItem.quantidade, alreadyImported),
        mappedData: {
          product_subtype_id: existingItem?.product_subtype_id ?? undefined,
          name: xmlItem.descricao,
          sku: xmlItem.codigo,
          barcode: xmlItem.ean,
          cost_price: xmlItem.valorUnitario,
          sale_price: xmlItem.valorUnitario, // Default to unit price, user can adjust
          unit: xmlItem.unidade || "UN",
          ncm: xmlItem.ncm,
          cfop: xmlItem.cfop,
          cest: xmlItem.cest,
          gtin: xmlItem.ean || xmlItem.gtinTributavel,
          unidade_tributavel: xmlItem.unidadeTributavel || xmlItem.unidade,
          valor_total_tributos: xmlItem.valorTotalTributos,
          aliquota_aproximada_tributos:
            typeof xmlItem.valorTotalTributos === "number" && xmlItem.valorTotal > 0
              ? Number(((xmlItem.valorTotalTributos / xmlItem.valorTotal) * 100).toFixed(4))
              : undefined,
          fonte_valor_total_tributos: xmlItem.valorTotalTributos != null ? "imported_xml" : undefined,
          origem: xmlItem.origem,
          icms_tax_situation: xmlItem.icmsSituacaoTributaria,
          icms_base_calculo: xmlItem.icmsBaseCalculo,
          icms_aliquota: xmlItem.icmsAliquota,
          icms_valor: xmlItem.icmsValor,
          icms_modalidade_base_calculo: xmlItem.icmsModalidadeBaseCalculo,
          ipi_tax_situation: xmlItem.ipiSituacaoTributaria,
          ipi_enquadramento: xmlItem.ipiEnquadramento,
          ipi_base_calculo: xmlItem.ipiBaseCalculo,
          ipi_aliquota: xmlItem.ipiAliquota,
          ipi_valor: xmlItem.ipiValor,
          pis_tax_situation: xmlItem.pisSituacaoTributaria,
          pis_base_calculo: xmlItem.pisBaseCalculo,
          pis_aliquota: xmlItem.pisAliquota,
          pis_valor: xmlItem.pisValor,
          cofins_tax_situation: xmlItem.cofinsSituacaoTributaria,
          cofins_base_calculo: xmlItem.cofinsBaseCalculo,
          cofins_aliquota: xmlItem.cofinsAliquota,
          cofins_valor: xmlItem.cofinsValor,
          ibs_cbs_situacao_tributaria: xmlItem.ibsCbsSituacaoTributaria,
          ibs_cbs_classificacao_tributaria: xmlItem.ibsCbsClassificacaoTributaria,
          ibs_cbs_base_calculo: xmlItem.ibsCbsBaseCalculo,
          cbs_aliquota: xmlItem.cbsAliquota,
          cbs_valor: xmlItem.cbsValor,
          ibs_uf_aliquota: xmlItem.ibsUfAliquota,
          ibs_uf_valor: xmlItem.ibsUfValor,
          ibs_mun_aliquota: xmlItem.ibsMunAliquota,
          ibs_mun_valor: xmlItem.ibsMunValor,
          ibs_valor_total: xmlItem.ibsValorTotal,
          is_valor: xmlItem.isValor,
        },
      });
    });
    // The duplicate check resolves after the table mounts: when the flag flips,
    // the default entry quantities must win over whatever was preserved.
    const duplicateFlagChanged = previousAlreadyImported.current !== alreadyImported;
    previousAlreadyImported.current = alreadyImported;
    setSelections((prev) => {
      // Keep what the user already typed when the existing-items list loads later.
      initialSelections.forEach((selection, codigo) => {
        const previous = prev.get(codigo);
        if (!previous) return;
        if (!duplicateFlagChanged) selection.stockQuantity = previous.stockQuantity;
        selection.mappedData.sale_price = previous.mappedData.sale_price;
      });
      return initialSelections;
    });
  }, [xmlItems, existingItems, alreadyImported]);

  // Notify parent of selection changes
  useEffect(() => {
    onSelectionChange(Array.from(selections.values()));
  }, [selections, onSelectionChange]);

  const filteredItems = useMemo(() => {
    if (!searchTerm) return xmlItems;
    const term = searchTerm.toLowerCase();
    return xmlItems.filter(
      (item) =>
        item.descricao.toLowerCase().includes(term) ||
        item.codigo.toLowerCase().includes(term) ||
        (item.ean && item.ean.toLowerCase().includes(term))
    );
  }, [xmlItems, searchTerm]);

  const handleToggleSelection = (codigo: string) => {
    setSelections((prev) => {
      const newSelections = new Map(prev);
      const current = newSelections.get(codigo);
      if (!current) return prev;

      const newAction =
        current.action === "skip" ? (current.existingItemId ? "update" : "create") : "skip";

      newSelections.set(codigo, {
        ...current,
        action: newAction,
      });
      return newSelections;
    });
  };

  const handleActionChange = (codigo: string, action: "create" | "update" | "skip") => {
    setSelections((prev) => {
      const newSelections = new Map(prev);
      const current = newSelections.get(codigo);
      if (!current) return prev;

      newSelections.set(codigo, {
        ...current,
        action,
      });
      return newSelections;
    });
  };

  const handleMappedDataChange = (
    codigo: string,
    field: keyof XmlImportItemSelection["mappedData"],
    value: string | number
  ) => {
    setSelections((prev) => {
      const newSelections = new Map(prev);
      const current = newSelections.get(codigo);
      if (!current) return prev;

      newSelections.set(codigo, {
        ...current,
        mappedData: {
          ...current.mappedData,
          [field]: value,
        },
      });
      return newSelections;
    });
  };

  const handleStockQuantityChange = (codigo: string, raw: string) => {
    setSelections((prev) => {
      const next = new Map(prev);
      const current = next.get(codigo);
      if (!current) return prev;
      next.set(codigo, { ...current, stockQuantity: raw === "" ? 0 : Number(raw) });
      return next;
    });
  };

  const formatCurrencyValue = (value: number) =>
    new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

  const formatPercentValue = (value: number) =>
    new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

  const selectedCount = Array.from(selections.values()).filter(
    (s) => s.action !== "skip"
  ).length;

  return (
    <div className="min-w-0 space-y-4">
      <div className="flex items-center justify-between">
        <SearchField
          className="flex-1 max-w-sm"
          label={t("xmlImportDialog.table.search")}
          clearLabel={t("common:clearSearch")}
          placeholder={t("xmlImportDialog.table.search")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm("")}
        />
        <Badge variant="secondary">
          {t("xmlImportDialog.table.selectedBadge", { selected: selectedCount, total: xmlItems.length })}
        </Badge>
      </div>

      <div className="min-w-0 overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={selectedCount === filteredItems.length && selectedCount > 0}
                  onCheckedChange={(checked) => {
                    filteredItems.forEach((item) => {
                      handleActionChange(
                        item.codigo,
                        checked ? (selections.get(item.codigo)?.existingItemId ? "update" : "create") : "skip"
                      );
                    });
                  }}
                />
              </TableHead>
              <TableHead>{t("xmlImportDialog.table.item")}</TableHead>
              <TableHead className="text-right">{t("xmlImportDialog.table.quantity")}</TableHead>
              {showStock && (
                <TableHead className="text-right">{t("xmlImportDialog.table.stockEntry")}</TableHead>
              )}
              <TableHead className="text-right">{t("xmlImportDialog.table.unitValue")}</TableHead>
              <TableHead>{t("xmlImportDialog.table.action")}</TableHead>
              <TableHead>{t("catalogPage.subtypeLabel")}</TableHead>
              <TableHead className="text-right">{t("xmlImportDialog.table.salePrice")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showStock ? 8 : 7} className="text-center text-muted-foreground py-8">
                  {t("xmlImportDialog.table.empty")}
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((xmlItem) => {
                const selection = selections.get(xmlItem.codigo);
                if (!selection) return null;

                const isSelected = selection.action !== "skip";
                const salePrice = selection.mappedData.sale_price ?? 0;
                const approximateTributeRate =
                  typeof xmlItem.valorTotalTributos === "number" && xmlItem.valorTotal > 0
                    ? (xmlItem.valorTotalTributos / xmlItem.valorTotal) * 100
                    : null;

                return (
                  <TableRow
                    key={xmlItem.codigo}
                    className={isSelected ? "bg-muted/50" : ""}
                  >
                    <TableCell>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleToggleSelection(xmlItem.codigo)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{xmlItem.descricao}</div>
                      {selection.existingItemId && (
                        <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          <AlertCircle className="h-3 w-3" />
                          {t("xmlImportDialog.table.existing")}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mt-1 space-x-2">
                        {showFiscal && <span>NCM: {xmlItem.ncm || "—"}</span>}
                        {showFiscal && <span>CFOP: {xmlItem.cfop || "—"}</span>}
                        <span>{t("xmlImportDialog.table.code")}: {xmlItem.codigo}</span>
                        <span>EAN: {xmlItem.ean || "—"}</span>
                        {showFiscal && xmlItem.valorTotalTributos != null && (
                          <span>
                            {t("taxes.xmlImport.approximateTributes")}: R$ {formatCurrencyValue(xmlItem.valorTotalTributos)}
                            {approximateTributeRate != null ? ` (${formatPercentValue(approximateTributeRate)}%)` : ""}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{xmlItem.quantidade}</TableCell>
                    {showStock && (
                      <TableCell className="text-right">
                        {isSelected && (
                          <Input
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="any"
                            className="w-24 text-right"
                            aria-label={t("xmlImportDialog.table.stockEntryAria", { item: xmlItem.descricao })}
                            value={selection.stockQuantity ? selection.stockQuantity : ""}
                            placeholder="0"
                            onChange={(e) => handleStockQuantityChange(xmlItem.codigo, e.target.value)}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <span className="text-sm text-muted-foreground">R$</span>
                      <span className="ml-1 font-medium">{formatCurrencyValue(xmlItem.valorUnitario)}</span>
                    </TableCell>
                    <TableCell>
                      {isSelected ? (
                        <Select
                          value={selection.action}
                          onValueChange={(value) =>
                            handleActionChange(xmlItem.codigo, value as "create" | "update" | "skip")
                          }
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="create">
                              <div className="flex items-center gap-2">
                                <Package className="h-4 w-4" />
                                {t("xmlImportDialog.table.create")}
                              </div>
                            </SelectItem>
                            {selection.existingItemId && (
                              <SelectItem value="update">{t("xmlImportDialog.table.update")}</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-muted-foreground text-sm">{t("xmlImportDialog.table.skip")}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isSelected && (
                        <Select
                          value={selection.mappedData.product_subtype_id ?? ""}
                          onValueChange={(value) =>
                            handleMappedDataChange(
                              xmlItem.codigo,
                              "product_subtype_id",
                              value || undefined
                            )
                          }
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder={t("xmlImportDialog.table.selectPlaceholder")} />
                          </SelectTrigger>
                          <SelectContent>
                            {productSubtypes.map((st) => (
                              <SelectItem key={st.id} value={st.id}>
                                {st.name.charAt(0).toUpperCase() + st.name.slice(1)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {isSelected && (
                        <div className="relative inline-flex items-center">
                          <span className="absolute left-3 text-sm text-muted-foreground pointer-events-none">R$</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            className="w-32 text-right"
                            style={{ paddingLeft: "2.25rem" }}
                            value={salePrice === 0 ? "" : salePrice}
                            placeholder="0,00"
                            onChange={(e) =>
                              handleMappedDataChange(
                                xmlItem.codigo,
                                "sale_price",
                                parseFloat(e.target.value) || 0
                              )
                            }
                          />
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
